import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Users,
  Calendar,
  Search,
  Mail,
  Phone,
  MapPin,
  Globe,
  Check,
  X,
  Trash2,
  ChevronDown,
  ChevronUp,
  Clock,
  Star,
  MessageSquare,
  Settings as SettingsIcon,
  Map,
  Upload,
  ImageIcon,
  Send,
} from "lucide-react";

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [unreadChats, setUnreadChats] = useState(0);

  const refresh = async () => {
    const [s, u, b, r] = await Promise.all([
      api.get("/admin/stats").then((r) => r.data),
      api.get("/admin/users").then((r) => r.data),
      api.get("/admin/bookings").then((r) => r.data),
      api.get("/admin/reviews").then((r) => r.data),
    ]);
    setStats(s);
    setUsers(u);
    setBookings(b);
    setReviews(r);
  };

  const pollUnread = () =>
    api
      .get("/admin/chats")
      .then((r) => setUnreadChats(r.data.filter((s) => s.admin_unread).length))
      .catch(() => {});

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { replace: true, state: { from: { pathname: "/admin" } } });
      return;
    }
    if (user.role !== "admin") {
      navigate("/account", { replace: true });
      return;
    }
    refresh();
    pollUnread();
    const t = setInterval(pollUnread, 10000);
    return () => clearInterval(t);
  }, [loading, user, navigate]);

  if (loading || !user || user.role !== "admin" || stats === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleStatus = async (booking_id, status) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${booking_id}`, { status });
      setBookings((prev) => prev.map((b) => (b.booking_id === booking_id ? { ...b, ...data } : b)));
      const newStats = await api.get("/admin/stats").then((r) => r.data);
      setStats(newStats);
      toast.success(`Booking ${status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleDelete = async (booking_id) => {
    if (!window.confirm("Delete this booking? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/bookings/${booking_id}`);
      setBookings((prev) => prev.filter((b) => b.booking_id !== booking_id));
      const newStats = await api.get("/admin/stats").then((r) => r.data);
      setStats(newStats);
      toast.success("Booking deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleReviewStatus = async (review_id, status) => {
    try {
      const { data } = await api.put(`/admin/reviews/${review_id}/status`, { status });
      setReviews((prev) => prev.map((r) => (r.review_id === review_id ? data : r)));
      toast.success(`Review ${status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleDeleteReview = async (review_id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/admin/reviews/${review_id}`);
      setReviews((prev) => prev.filter((r) => r.review_id !== review_id));
      const newStats = await api.get("/admin/stats").then((r) => r.data);
      setStats(newStats);
      toast.success("Review deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const q = search.toLowerCase();
  const filteredUsers = users.filter(
    (u) =>
      !q ||
      (u.email && u.email.toLowerCase().includes(q)) ||
      (u.name && u.name.toLowerCase().includes(q))
  );
  const filteredBookings = bookings
    .filter((b) => statusFilter === "all" || b.status === statusFilter)
    .filter(
      (b) =>
        !q ||
        `${b.first_name} ${b.last_name}`.toLowerCase().includes(q) ||
        (b.email && b.email.toLowerCase().includes(q)) ||
        (b.interest && b.interest.toLowerCase().includes(q))
    );

  return (
    <div className="min-h-screen bg-background font-sans flex flex-col">
      <Navbar />
      <main className="flex-grow pt-28 pb-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-8">
            <h1
              className="text-3xl md:text-4xl font-serif font-bold text-primary"
              data-testid="admin-title"
            >
              Admin <span className="text-secondary">Dashboard</span>
            </h1>
            <p className="text-muted-foreground">All registered users and booking requests</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            <StatCard icon={Users} label="Total Users" value={stats.total_users} />
            <StatCard
              icon={Calendar}
              label="Bookings"
              value={stats.total_bookings}
              sub={`${stats.confirmed_bookings || 0} confirmed · ${stats.pending_bookings || 0} pending`}
            />
            <StatCard icon={Star} label="Reviews" value={stats.total_reviews || 0} />
            <StatCard icon={Globe} label="Google Sign-ups" value={stats.google_users} />
          </div>

          {/* Search */}
          <div className="relative mb-6 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, interest…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search"
              className="pl-10 bg-white"
            />
          </div>

          <Tabs defaultValue="bookings">
            <TabsList className="bg-white border border-primary/10 flex flex-wrap h-auto w-full sm:w-auto justify-start gap-1">
              <TabsTrigger value="bookings" data-testid="admin-tab-bookings">
                <Calendar className="w-4 h-4 mr-2" /> Bookings ({filteredBookings.length})
              </TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-tab-users">
                <Users className="w-4 h-4 mr-2" /> Users ({filteredUsers.length})
              </TabsTrigger>
              <TabsTrigger value="reviews" data-testid="admin-tab-reviews">
                <MessageSquare className="w-4 h-4 mr-2" /> Reviews ({(reviews || []).length})
              </TabsTrigger>
              <TabsTrigger value="chats" data-testid="admin-tab-chats" className="relative">
                <MessageSquare className="w-4 h-4 mr-2" /> Chats
                {unreadChats > 0 && (
                  <span
                    data-testid="chats-unread-badge"
                    className="ml-2 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                  >
                    {unreadChats}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="destinations" data-testid="admin-tab-destinations">
                <Map className="w-4 h-4 mr-2" /> Destinations
              </TabsTrigger>
              <TabsTrigger value="proofs" data-testid="admin-tab-proofs">
                <ImageIcon className="w-4 h-4 mr-2" /> Proofs
              </TabsTrigger>
              <TabsTrigger value="contact" data-testid="admin-tab-contact">
                <SettingsIcon className="w-4 h-4 mr-2" /> Contact
              </TabsTrigger>
            </TabsList>

            <TabsContent value="bookings" className="mt-6 space-y-3" data-testid="admin-bookings-list">
              <div className="flex gap-2 flex-wrap">
                {["all", "pending", "confirmed", "cancelled"].map((s) => (
                  <Button
                    key={s}
                    variant={statusFilter === s ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatusFilter(s)}
                    data-testid={`admin-filter-${s}`}
                    className={`rounded-full capitalize ${
                      statusFilter === s ? "bg-primary text-white" : "border-primary/20"
                    }`}
                  >
                    {s}
                  </Button>
                ))}
              </div>

              {filteredBookings.map((b) => (
                <BookingRow
                  key={b.booking_id}
                  b={b}
                  onConfirm={() => handleStatus(b.booking_id, "confirmed")}
                  onCancel={() => handleStatus(b.booking_id, "cancelled")}
                  onReopen={() => handleStatus(b.booking_id, "pending")}
                  onDelete={() => handleDelete(b.booking_id)}
                />
              ))}
              {filteredBookings.length === 0 && (
                <p className="text-center text-muted-foreground py-12">No bookings match your filters.</p>
              )}
            </TabsContent>

            <TabsContent value="users" className="mt-6 space-y-3" data-testid="admin-users-list">
              {filteredUsers.map((u) => (
                <UserRow key={u.user_id} u={u} />
              ))}
              {filteredUsers.length === 0 && (
                <p className="text-center text-muted-foreground py-12">No users match your search.</p>
              )}
            </TabsContent>

            <TabsContent value="reviews" className="mt-6 space-y-3" data-testid="admin-reviews-list">
              {(reviews || []).map((r) => (
                <ReviewAdminRow
                  key={r.review_id}
                  r={r}
                  onStatus={(status) => handleReviewStatus(r.review_id, status)}
                  onDelete={() => handleDeleteReview(r.review_id)}
                />
              ))}
              {(reviews || []).length === 0 && (
                <p className="text-center text-muted-foreground py-12">No reviews yet.</p>
              )}
            </TabsContent>

            <TabsContent value="chats" className="mt-6" data-testid="admin-chats-tab">
              <ChatsManager />
            </TabsContent>

            <TabsContent value="destinations" className="mt-6" data-testid="admin-destinations-tab">
              <DestinationsManager />
            </TabsContent>

            <TabsContent value="proofs" className="mt-6" data-testid="admin-proofs-tab">
              <ProofsManager />
            </TabsContent>

            <TabsContent value="contact" className="mt-6" data-testid="admin-contact-tab">
              <ContactSettingsForm />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ChatsManager() {
  const [sessions, setSessions] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const bottomRef = React.useRef(null);

  const loadSessions = () =>
    api.get("/admin/chats").then((r) => setSessions(r.data)).catch(() => {});

  useEffect(() => {
    loadSessions();
    const t = setInterval(loadSessions, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!activeId) return;
    const load = () =>
      api.get(`/chat/${activeId}/messages`).then((r) => setMessages(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [activeId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleSend = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || !activeId) return;
    setText("");
    try {
      const { data } = await api.post(`/admin/chats/${activeId}/messages`, { text: t });
      setMessages((m) => [...m, data]);
      loadSessions();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setText(t);
    }
  };

  const handleDeleteChat = async (session_id) => {
    if (!window.confirm("Delete this chat conversation?")) return;
    try {
      await api.delete(`/admin/chats/${session_id}`);
      setSessions((prev) => prev.filter((s) => s.session_id !== session_id));
      if (activeId === session_id) {
        setActiveId(null);
        setMessages([]);
      }
      toast.success("Chat deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (sessions === null) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const active = sessions.find((s) => s.session_id === activeId);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
      {/* Session list */}
      <div className="space-y-2 lg:col-span-1" data-testid="admin-chat-sessions">
        <h3 className="font-serif text-lg font-bold text-primary mb-2">
          Conversations ({sessions.length})
        </h3>
        {sessions.length === 0 && (
          <Card className="border-primary/10 border-dashed">
            <CardContent className="p-6 text-center text-sm text-muted-foreground">
              No chats yet. When customers use the site chat, they'll appear here.
            </CardContent>
          </Card>
        )}
        {sessions.map((s) => (
          <Card
            key={s.session_id}
            onClick={() => setActiveId(s.session_id)}
            data-testid={`admin-chat-session-${s.session_id}`}
            className={`border-primary/10 cursor-pointer transition-colors ${
              activeId === s.session_id ? "ring-2 ring-primary/40" : "hover:bg-primary/5"
            } ${s.admin_unread ? "ring-2 ring-amber-300" : ""}`}
          >
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <h4 className="font-bold text-primary text-sm truncate">{s.name}</h4>
                  {s.admin_unread && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs">new</Badge>
                  )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteChat(s.session_id);
                  }}
                  data-testid={`admin-chat-delete-${s.session_id}`}
                  className="text-red-500 hover:text-red-700 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-muted-foreground truncate mt-1">
                {s.last_sender === "admin" ? "You: " : ""}
                {s.last_message || "No messages yet"}
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">
                {s.email ? `${s.email} · ` : ""}
                {new Date(s.last_message_at).toLocaleString()}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Conversation */}
      <Card className="border-primary/10 lg:col-span-2">
        <CardContent className="p-0 flex flex-col" style={{ height: "30rem" }}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              Select a conversation to reply
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-primary/10">
                <h4 className="font-serif font-bold text-primary">{active.name}</h4>
                <p className="text-xs text-muted-foreground">
                  {active.email || "No email"} · started {new Date(active.created_at).toLocaleString()}
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-primary-foreground/40" data-testid="admin-chat-messages">
                {messages.map((m) => (
                  <div key={m.message_id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm whitespace-pre-line ${
                        m.sender === "admin"
                          ? "bg-primary text-white rounded-br-sm"
                          : "bg-white border border-primary/10 rounded-bl-sm"
                      }`}
                    >
                      {m.text}
                      <div className={`text-[10px] mt-1 ${m.sender === "admin" ? "text-white/70" : "text-muted-foreground"}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={handleSend} className="p-3 border-t border-primary/10 flex gap-2">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Reply to customer…"
                  data-testid="admin-chat-reply-input"
                  className="bg-white flex-1"
                />
                <Button
                  type="submit"
                  size="icon"
                  data-testid="admin-chat-send-btn"
                  className="bg-primary text-white hover:bg-primary/90 rounded-full shrink-0"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ProofsManager() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    api.get("/proofs").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 1200;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        if (dataUrl.length > 1_800_000) {
          toast.error("Photo is too large — pick a smaller one");
          return;
        }
        setPhoto(dataUrl);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!photo) {
      toast.error("Please upload a photo");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/proofs", {
        title: title.trim(),
        text: text.trim(),
        image: photo,
      });
      setItems((prev) => [data, ...(prev || [])]);
      setTitle("");
      setText("");
      setPhoto(null);
      toast.success("Proof added! It's now visible on the home page.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (proof_id) => {
    if (!window.confirm("Delete this proof?")) return;
    try {
      await api.delete(`/admin/proofs/${proof_id}`);
      setItems((prev) => prev.filter((p) => p.proof_id !== proof_id));
      toast.success("Proof deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      <Card className="border-primary/10">
        <CardContent className="p-6">
          <h3 className="font-serif text-xl font-bold text-primary mb-1">Add Proof</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Proofs appear in an animated slider on the home page, above the Reviews section.
          </p>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Title *</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Happy family from Germany — Sigiriya trip"
                data-testid="proof-title-input"
                className="bg-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Text</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                placeholder="Describe the trip, payment proof, customer feedback…"
                data-testid="proof-text-input"
                className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Photo *</label>
              {photo ? (
                <div className="relative inline-block">
                  <img src={photo} alt="preview" className="h-36 rounded-lg border border-primary/10 object-cover" />
                  <button
                    type="button"
                    onClick={() => setPhoto(null)}
                    data-testid="proof-photo-remove"
                    className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="proof-photo"
                  data-testid="proof-photo-upload"
                  className="flex items-center gap-2 border-2 border-dashed border-primary/20 rounded-lg px-4 py-6 cursor-pointer hover:border-primary/40 text-sm text-muted-foreground justify-center"
                >
                  <Upload className="w-4 h-4" /> Click to upload photo
                </label>
              )}
              <input id="proof-photo" type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
            </div>
            <Button
              type="submit"
              disabled={saving}
              data-testid="proof-add-btn"
              className="bg-primary text-white hover:bg-primary/90 rounded-full px-8"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Add Proof
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h3 className="font-serif text-lg font-bold text-primary">
          Published Proofs ({(items || []).length})
        </h3>
        {items === null ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <Card className="border-primary/10 border-dashed">
            <CardContent className="p-8 text-center text-muted-foreground text-sm">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No proofs yet. Add your first one!
            </CardContent>
          </Card>
        ) : (
          items.map((p) => (
            <Card key={p.proof_id} className="border-primary/10" data-testid={`admin-proof-${p.proof_id}`}>
              <CardContent className="p-3 flex items-center gap-4">
                <img src={p.image} alt={p.title} className="w-20 h-16 rounded-lg object-cover shrink-0 border border-primary/10" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-serif font-bold text-primary truncate">{p.title}</h4>
                  <p className="text-xs text-muted-foreground truncate mt-1">{p.text}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRemove(p.proof_id)}
                  data-testid={`proof-delete-${p.proof_id}`}
                  className="rounded-full text-red-600 hover:bg-red-50 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

const DEST_CATEGORIES = ["Heritage", "Beach", "Wildlife", "Nature", "Culture", "City"];

function DestinationsManager() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    location: "",
    category: "Nature",
    description: "",
    rating: "4.5",
  });
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    api.get("/destinations").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 1200;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        if (dataUrl.length > 1_800_000) {
          toast.error("Photo is too large — pick a smaller one");
          return;
        }
        setPhoto(dataUrl);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.location.trim()) {
      toast.error("Name and location are required");
      return;
    }
    if (!photo) {
      toast.error("Please upload a photo for the destination");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/destinations", {
        name: form.name.trim(),
        location: form.location.trim(),
        category: form.category,
        description: form.description.trim(),
        rating: parseFloat(form.rating) || 4.5,
        image: photo,
      });
      setItems((prev) => [data, ...(prev || [])]);
      setForm({ name: "", location: "", category: "Nature", description: "", rating: "4.5" });
      setPhoto(null);
      toast.success("Destination added! It's now live on the Plan Trip page.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (place_id) => {
    if (!window.confirm("Delete this destination?")) return;
    try {
      await api.delete(`/admin/destinations/${place_id}`);
      setItems((prev) => prev.filter((d) => d.place_id !== place_id));
      toast.success("Destination deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      {/* Add form */}
      <Card className="border-primary/10">
        <CardContent className="p-6">
          <h3 className="font-serif text-xl font-bold text-primary mb-1">Add New Destination</h3>
          <p className="text-sm text-muted-foreground mb-6">
            New destinations appear on the Plan Trip page instantly.
          </p>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Name *</label>
                <Input
                  value={form.name}
                  onChange={set("name")}
                  placeholder="e.g. Ella Rock"
                  data-testid="dest-name-input"
                  className="bg-white"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Location *</label>
                <Input
                  value={form.location}
                  onChange={set("location")}
                  placeholder="e.g. Ella"
                  data-testid="dest-location-input"
                  className="bg-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Category</label>
                <select
                  value={form.category}
                  onChange={set("category")}
                  data-testid="dest-category-select"
                  className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm"
                >
                  {DEST_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Rating (1–5)</label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  step="0.1"
                  value={form.rating}
                  onChange={set("rating")}
                  data-testid="dest-rating-input"
                  className="bg-white"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <textarea
                value={form.description}
                onChange={set("description")}
                rows={3}
                placeholder="Short description of the place…"
                data-testid="dest-description-input"
                className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Photo *</label>
              {photo ? (
                <div className="relative inline-block">
                  <img
                    src={photo}
                    alt="preview"
                    className="h-36 rounded-lg border border-primary/10 object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setPhoto(null)}
                    data-testid="dest-photo-remove"
                    className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="dest-photo"
                  data-testid="dest-photo-upload"
                  className="flex items-center gap-2 border-2 border-dashed border-primary/20 rounded-lg px-4 py-6 cursor-pointer hover:border-primary/40 text-sm text-muted-foreground justify-center"
                >
                  <Upload className="w-4 h-4" /> Click to upload photo
                </label>
              )}
              <input
                id="dest-photo"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
              />
            </div>
            <Button
              type="submit"
              disabled={saving}
              data-testid="dest-add-btn"
              className="bg-primary text-white hover:bg-primary/90 rounded-full px-8"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Add Destination
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Existing custom destinations */}
      <div className="space-y-3">
        <h3 className="font-serif text-lg font-bold text-primary">
          Your Added Destinations ({(items || []).length})
        </h3>
        {items === null ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <Card className="border-primary/10 border-dashed">
            <CardContent className="p-8 text-center text-muted-foreground text-sm">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No custom destinations yet. Add your first one!
            </CardContent>
          </Card>
        ) : (
          items.map((d) => (
            <Card key={d.place_id} className="border-primary/10" data-testid={`admin-dest-${d.place_id}`}>
              <CardContent className="p-3 flex items-center gap-4">
                <img
                  src={d.image}
                  alt={d.name}
                  className="w-20 h-16 rounded-lg object-cover shrink-0 border border-primary/10"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-serif font-bold text-primary truncate">{d.name}</h4>
                    <Badge variant="outline" className="text-xs border-primary/20">{d.category}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" /> {d.location} · ★ {d.rating}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRemove(d.place_id)}
                  data-testid={`dest-delete-${d.place_id}`}
                  className="rounded-full text-red-600 hover:bg-red-50 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

function ContactSettingsForm() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => setForm({}));
  }, []);

  if (form === null) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        address: form.address || "",
        phone1: form.phone1 || "",
        phone2: form.phone2 || "",
        email: form.email || "",
        whatsapp: form.whatsapp || "",
      });
      toast.success("Contact details updated. Changes are live on the site.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-primary/10 max-w-2xl">
      <CardContent className="p-6">
        <h3 className="font-serif text-xl font-bold text-primary mb-1">Site Contact Details</h3>
        <p className="text-sm text-muted-foreground mb-6">
          These details are shown in the website footer, booking page and WhatsApp button.
        </p>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Address</label>
            <Input
              value={form.address || ""}
              onChange={set("address")}
              data-testid="settings-address-input"
              className="bg-white"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Phone 1</label>
              <Input
                value={form.phone1 || ""}
                onChange={set("phone1")}
                data-testid="settings-phone1-input"
                className="bg-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Phone 2 (optional)</label>
              <Input
                value={form.phone2 || ""}
                onChange={set("phone2")}
                data-testid="settings-phone2-input"
                className="bg-white"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Contact Email</label>
            <Input
              type="email"
              value={form.email || ""}
              onChange={set("email")}
              data-testid="settings-email-input"
              className="bg-white"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">
              WhatsApp Number (digits only, with country code e.g. 94761609626)
            </label>
            <Input
              value={form.whatsapp || ""}
              onChange={set("whatsapp")}
              data-testid="settings-whatsapp-input"
              className="bg-white"
            />
          </div>
          <Button
            type="submit"
            disabled={saving}
            data-testid="settings-save-btn"
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-8"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <Card className="border-primary/10">
      <CardContent className="p-5 flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Icon className="w-6 h-6" />
        </div>
        <div className="min-w-0">
          <div className="text-2xl font-serif font-bold text-primary">{value}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

function UserRow({ u }) {
  const initials = (u.name || u.email)
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <Card className="border-primary/10">
      <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-4">
        <Avatar className="w-12 h-12">
          <AvatarImage src={u.picture} alt={u.name} />
          <AvatarFallback className="bg-primary text-white">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif font-bold text-primary truncate">{u.name || "—"}</h3>
            {u.role === "admin" && (
              <Badge className="bg-secondary/20 text-secondary-foreground border-secondary/30">admin</Badge>
            )}
            {(u.providers || []).map((p) => (
              <Badge key={p} variant="outline" className="text-xs capitalize border-primary/20">
                {p === "google" ? "Google" : "Email"}
              </Badge>
            ))}
          </div>
          <div className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 mt-1">
            <span className="flex items-center gap-1">
              <Mail className="w-3 h-3" /> {u.email}
            </span>
            {u.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" /> {u.phone}
              </span>
            )}
            {u.country && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {u.country}
              </span>
            )}
          </div>
        </div>
        <div className="text-xs text-muted-foreground shrink-0">
          Joined {new Date(u.created_at).toLocaleDateString()}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { cls: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
    confirmed: { cls: "bg-green-100 text-green-800 border-green-300", icon: Check },
    cancelled: { cls: "bg-red-100 text-red-800 border-red-300", icon: X },
  };
  const conf = map[status] || map.pending;
  const Icon = conf.icon;
  return (
    <Badge className={`${conf.cls} capitalize gap-1`} variant="outline">
      <Icon className="w-3 h-3" />
      {status}
    </Badge>
  );
}

function BookingRow({ b, onConfirm, onCancel, onReopen, onDelete }) {
  const [open, setOpen] = useState(false);
  const status = b.status || "pending";
  return (
    <Card className="border-primary/10">
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-serif font-bold text-primary">
                {b.first_name} {b.last_name}
              </h3>
              <StatusBadge status={status} />
              {b.user ? (
                <Badge variant="outline" className="text-xs border-green-300 text-green-700">
                  registered
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs border-gray-300 text-gray-600">
                  guest
                </Badge>
              )}
            </div>
            <div className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
              <a href={`mailto:${b.email}`} className="flex items-center gap-1 hover:text-primary">
                <Mail className="w-3 h-3" /> {b.email}
              </a>
              {b.phone && (
                <a
                  href={`tel:${(b.country_code || "") + b.phone}`}
                  className="flex items-center gap-1 hover:text-primary"
                >
                  <Phone className="w-3 h-3" /> {b.country_code || ""} {b.phone}
                </a>
              )}
              {b.country && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {b.country}
                </span>
              )}
            </div>
            <div className="text-sm text-muted-foreground mt-2">
              {b.interest || "—"} • {b.duration || "—"} • Travel: {b.travel_date || "Not set"}
            </div>
          </div>
          <div className="text-xs text-muted-foreground shrink-0 md:text-right">
            {new Date(b.created_at).toLocaleString()}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-primary/5">
          {status !== "confirmed" && (
            <Button
              size="sm"
              onClick={onConfirm}
              data-testid={`booking-confirm-${b.booking_id}`}
              className="bg-green-600 hover:bg-green-700 text-white rounded-full"
            >
              <Check className="w-4 h-4 mr-1" /> Confirm
            </Button>
          )}
          {status !== "cancelled" && status !== "pending" && (
            <Button
              size="sm"
              variant="outline"
              onClick={onReopen}
              data-testid={`booking-reopen-${b.booking_id}`}
              className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <Clock className="w-4 h-4 mr-1" /> Mark pending
            </Button>
          )}
          {status !== "cancelled" && (
            <Button
              size="sm"
              variant="outline"
              onClick={onCancel}
              data-testid={`booking-cancel-${b.booking_id}`}
              className="rounded-full border-red-300 text-red-700 hover:bg-red-50"
            >
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setOpen((o) => !o)}
            data-testid={`booking-toggle-${b.booking_id}`}
            className="rounded-full ml-auto"
          >
            {open ? (
              <>
                <ChevronUp className="w-4 h-4 mr-1" /> Hide details
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-1" /> View details
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            data-testid={`booking-delete-${b.booking_id}`}
            className="rounded-full text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>

        {open && (
          <div
            className="mt-4 pt-4 border-t border-primary/5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm"
            data-testid={`booking-details-${b.booking_id}`}
          >
            <Detail label="Booking ID" value={b.booking_id} />
            <Detail label="First name" value={b.first_name} />
            <Detail label="Last name" value={b.last_name} />
            <Detail label="Email" value={b.email} />
            <Detail label="Phone" value={`${b.country_code || ""} ${b.phone || "—"}`} />
            <Detail label="Country" value={b.country || "—"} />
            <Detail label="Travel date" value={b.travel_date || "Not set"} />
            <Detail label="Duration" value={b.duration || "—"} />
            <Detail label="Interest" value={b.interest || "—"} />
            <Detail label="Created" value={new Date(b.created_at).toLocaleString()} />
            {b.updated_at && (
              <Detail label="Updated" value={new Date(b.updated_at).toLocaleString()} />
            )}
            <div className="md:col-span-2">
              <p className="text-xs uppercase text-muted-foreground tracking-wider mb-1">
                Special requests
              </p>
              <p className="text-foreground whitespace-pre-line">
                {b.message || <span className="text-muted-foreground italic">None</span>}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase text-muted-foreground tracking-wider">{label}</p>
      <p className="text-foreground break-words">{value || "—"}</p>
    </div>
  );
}

function ReviewAdminRow({ r, onStatus, onDelete }) {
  const status = r.status || "pending";
  const statusStyles = {
    approved: "border-green-300 text-green-700 bg-green-50",
    pending: "border-amber-300 text-amber-700 bg-amber-50",
    hidden: "border-gray-300 text-gray-600 bg-gray-50",
  };
  return (
    <Card
      className={`border-primary/10 ${status === "pending" ? "ring-2 ring-amber-200" : ""}`}
      data-testid={`admin-review-${r.review_id}`}
    >
      <CardContent className="p-4 flex flex-col md:flex-row md:items-start gap-4">
        <Avatar className="w-12 h-12 shrink-0">
          <AvatarImage src={r.picture} alt={r.name} />
          <AvatarFallback className="bg-primary/10 text-primary">
            {(r.name || "?").charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif font-bold text-primary">{r.name}</h3>
            <div className="flex">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`w-4 h-4 ${n <= r.rating ? "fill-secondary text-secondary" : "text-gray-300"}`}
                />
              ))}
            </div>
            <Badge variant="outline" className={`text-xs capitalize ${statusStyles[status]}`}>
              {status}
            </Badge>
            {r.user_id ? (
              <Badge variant="outline" className="text-xs border-green-300 text-green-700">
                registered
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs border-gray-300 text-gray-600">
                guest
              </Badge>
            )}
          </div>
          <p className="text-sm text-foreground mt-2">{r.comment}</p>
          {r.photo && (
            <img
              src={r.photo}
              alt="review"
              className="mt-3 max-w-xs max-h-48 rounded-lg border border-primary/10 object-cover"
            />
          )}
          <p className="text-xs text-muted-foreground mt-2">
            {r.location ? `${r.location} • ` : ""}
            {new Date(r.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row md:flex-col gap-2 self-start">
          {status !== "approved" && (
            <Button
              size="sm"
              onClick={() => onStatus("approved")}
              data-testid={`review-approve-${r.review_id}`}
              className="rounded-full bg-green-600 hover:bg-green-700 text-white"
            >
              <Check className="w-4 h-4 mr-1" /> Approve
            </Button>
          )}
          {status !== "hidden" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onStatus("hidden")}
              data-testid={`review-hide-${r.review_id}`}
              className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <X className="w-4 h-4 mr-1" /> Hide
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            data-testid={`review-delete-${r.review_id}`}
            className="rounded-full text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
